import { API_BASE, authHeaders } from './apiAuth'
import type { ImageRef } from './imageRefs'
import { CREDITS_PER_USD, type BoardHandover } from './pictureService'

/**
 * Upscale, from any surface
 * -------------------------------------------------------------
 * The website's page, the board's card and the Miro panel all start the same
 * run and read the same answer (workers/api/src/routes/upscale.ts). The models
 * are the Worker's list, read once from /api/upscale/options, so a model added
 * there appears everywhere without anybody editing this.
 */

export interface UpscaleModel {
  key: string
  label: string
  /** One word: Fidelity keeps the picture as it is, Enhance rebuilds detail. */
  kind: 'Fidelity' | 'Enhance'
  blurb: string
  /** Output megapixels one Topaz credit buys. */
  mpPerCredit: number
  /** Whether face enhancement can be switched on for it. */
  face: boolean
  /** The most it enlarges in one go, where Topaz limits it (Wonder 3.5: 6). */
  maxFactor?: number
  /** Offers Refine: the same size, its detail rebuilt (Wonder 3.5, 2026-10-16). */
  refine?: boolean
  /**
   * Dollars a picture on top of Topaz: H1's redraw by Image 2.5 Sunburst
   * before it is enlarged (2026-10-16). About, as the generate page estimates.
   */
  extraUsd?: number
}

export interface UpscaleSize {
  /** '2k', '4k' or 'refine'. */
  value: string
  label: string
  /** The longer side, in pixels. 0 for Refine: the size it already is. */
  longEdge: number
}

/** Refine (2026-10-16): no new size, the detail rebuilt at the size it is. */
export const isRefine = (size: UpscaleSize | undefined): boolean => Boolean(size) && size!.longEdge === 0

/**
 * Whether a picture is worth sending at this size: smaller than it, or any
 * picture at all for Refine. A size not known yet is sent; the Worker measures.
 */
export function takes(size: UpscaleSize, picture: Dimensions): boolean {
  if (isRefine(size)) return true
  return !picture.width || !picture.height || Math.max(picture.width, picture.height) < size.longEdge
}

export interface UpscaleOptions {
  /** False until the Topaz key is set on the Worker. */
  ready: boolean
  models: UpscaleModel[]
  defaultModel: string
  sizes: UpscaleSize[]
  /** One Topaz credit, in dollars. */
  usdPerCredit: number
  maxPictures: number
}

/** Above this many times bigger, Low Resolution 2 is suggested. */
export const BIG_JUMP = 6
export const LOW_RES_MODEL = 'low-resolution-v2'

async function reason(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    if (typeof body.error === 'string' && body.error) return body.error
  } catch {
    /* the status is all there is */
  }
  return `Something went wrong (HTTP ${response.status})`
}

let asked: Promise<UpscaleOptions> | null = null

/** The models, sizes and price. Read once per visit. */
export function upscaleOptions(): Promise<UpscaleOptions> {
  if (!asked) {
    asked = (async () => {
      const response = await fetch(`${API_BASE}/api/upscale/options`, {
        headers: { Accept: 'application/json', ...(await authHeaders()) },
      })
      if (!response.ok) throw new Error(await reason(response))
      return (await response.json()) as UpscaleOptions
    })()
    asked.catch(() => {
      asked = null
    })
  }
  return asked
}

/** The longer side at the size chosen, the other in proportion. */
export function targetSize(width: number, height: number, longEdge: number): { width: number; height: number } {
  if (width >= height) return { width: longEdge, height: Math.max(1, Math.round((longEdge * height) / width)) }
  return { width: Math.max(1, Math.round((longEdge * width) / height)), height: longEdge }
}

/** The size asked of this model: as chosen, or as near as its ceiling allows. */
export function targetFor(
  model: UpscaleModel,
  width: number,
  height: number,
  longEdge: number,
): { width: number; height: number; capped: boolean } {
  const long = Math.max(width, height)
  if (model.maxFactor && longEdge / long > model.maxFactor) {
    return { ...targetSize(width, height, Math.floor(long * model.maxFactor)), capped: true }
  }
  return { ...targetSize(width, height, longEdge), capped: false }
}

/** How many of these pictures this model would stop short of the size. */
export function cappedBy(model: UpscaleModel, size: UpscaleSize, pictures: Dimensions[]): number {
  if (isRefine(size)) return 0
  return pictures.filter((one) => {
    const long = Math.max(one.width, one.height)
    return long > 0 && long < size.longEdge && Boolean(model.maxFactor) && size.longEdge / long > model.maxFactor!
  }).length
}

/** Topaz credits for one picture, as the Worker counts them. */
export function topazCredits(model: UpscaleModel, width: number, height: number): number {
  return Math.max(1, Math.ceil((width * height) / 1_000_000 / model.mpPerCredit - 1e-9))
}

/** A picture's pixels, where they are known. 0 when they are not. */
export interface Dimensions {
  width: number
  height: number
}

/**
 * Which of these pictures a size would actually enlarge.
 *
 * A picture already that big is skipped by the Worker, so the size is only
 * worth offering when at least one of them is smaller. A picture whose size is
 * not known yet is counted as smaller: the Worker measures it and says so.
 */
export function enlargedBy(size: UpscaleSize, pictures: Dimensions[]): number {
  return pictures.filter((one) => takes(size, one)).length
}

/**
 * About what a run will cost, in this platform's credits (1,000 = $1).
 *
 * "About", because a picture whose size is not known yet is counted at one
 * Topaz credit -- which is right for every Fidelity model and for Wonder 3.5
 * at 2K, and may be one short for Wonder 3.5 at 4K.
 */
export function estimateUpscale(
  options: UpscaleOptions,
  model: UpscaleModel,
  size: UpscaleSize,
  pictures: Dimensions[],
): number {
  let topaz = 0
  let extra = 0
  for (const one of pictures) {
    if (!one.width || !one.height) {
      topaz += 1
      extra += model.extraUsd ?? 0
      continue
    }
    if (!takes(size, one)) continue
    // Refine: priced at the size it already is.
    const target = isRefine(size)
      ? { width: one.width, height: one.height }
      : targetFor(model, one.width, one.height, size.longEdge)
    topaz += topazCredits(model, target.width, target.height)
    extra += model.extraUsd ?? 0
  }
  if (topaz === 0) return 0
  return Math.ceil((topaz * options.usdPerCredit + extra) * CREDITS_PER_USD)
}

/** The biggest jump any of these pictures would make. 0 when none is known. */
export function largestJump(size: UpscaleSize, pictures: Dimensions[]): number {
  return pictures.reduce((most, one) => {
    const edge = Math.max(one.width, one.height)
    return edge > 0 && edge < size.longEdge ? Math.max(most, size.longEdge / edge) : most
  }, 0)
}

export interface UpscaleAsk {
  projectId: string
  inputs: ImageRef[]
  model: string
  size: string
  face: boolean
  /** The Miro panel's placeholders, one per input, for the server to fill. */
  board?: BoardHandover
}

export interface UpscaleStarted {
  runId: string
  live: number
  limit: number
  /** The server puts the pictures on the board; the panel must not. */
  serverPlaces: boolean
}

export async function startUpscale(ask: UpscaleAsk): Promise<UpscaleStarted> {
  const response = await fetch(`${API_BASE}/api/skills/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({
      skill: 'skill-upscale',
      projectId: ask.projectId,
      inputs: ask.inputs,
      params: { model: ask.model, size: ask.size, face: ask.face, ...(ask.board ? { board: ask.board } : {}) },
    }),
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as { runId?: string; live?: number; limit?: number; board?: string }
  if (!body.runId) throw new Error('The run did not start')
  return {
    runId: body.runId,
    live: Number(body.live) || 0,
    limit: Number(body.limit) || 0,
    serverPlaces: body.board === 'server',
  }
}

/** What became of one picture that went in. */
export interface UpscaleResult {
  /** The picture that went in, as a name: `project:<id>`. */
  input: string
  /** What it became, as a name, when it worked. */
  output?: string
  error?: string
  width?: number
  height?: number
  /** Topaz credits this picture cost (routes/upscale.ts), once it is done. */
  credits?: number
  /** Smaller than asked: the model's ceiling on enlarging. */
  capped?: boolean
}

export interface UpscaledPicture {
  id: string
  store: 'library' | 'project'
  originalPath: string
  /** What to draw: a smaller copy where there is one (thumbs.ts), the original otherwise. */
  thumbnailPath: string
  title: string
  width: number
  height: number
}

export interface UpscaleRun {
  id: string
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  /** One per picture that went in, in the same order. */
  results: UpscaleResult[]
  /** The pictures made, by name. */
  pictures: Map<string, UpscaledPicture>
  error: string
  credits: number
  live: number
  limit: number
}

function readRun(raw: unknown, runId: string): UpscaleRun {
  const body = (raw ?? {}) as Record<string, unknown>
  const status = String(body.status ?? 'queued')
  const params = (body.params ?? {}) as Record<string, unknown>
  const results = Array.isArray(params.results)
    ? (params.results as Record<string, unknown>[]).map((one) => ({
        input: String(one.input ?? ''),
        ...(typeof one.output === 'string' ? { output: one.output } : {}),
        ...(typeof one.error === 'string' && one.error ? { error: one.error } : {}),
        ...(Number(one.width) ? { width: Number(one.width) } : {}),
        ...(Number(one.height) ? { height: Number(one.height) } : {}),
        ...(Number(one.credits) ? { credits: Number(one.credits) } : {}),
        ...(one.capped === true ? { capped: true } : {}),
      }))
    : []
  const pictures = new Map<string, UpscaledPicture>()
  if (Array.isArray(body.pictures)) {
    for (const one of body.pictures as Record<string, unknown>[]) {
      const store = one.store === 'library' ? 'library' : 'project'
      const id = String(one.id ?? '')
      pictures.set(`${store}:${id}`, {
        id,
        store,
        originalPath: String(one.originalPath ?? ''),
        thumbnailPath: String(one.thumbnailPath ?? '') || String(one.originalPath ?? ''),
        title: String(one.title ?? ''),
        width: Number(one.width) || 0,
        height: Number(one.height) || 0,
      })
    }
  }
  return {
    id: String(body.id ?? runId),
    status: status === 'running' || status === 'succeeded' || status === 'failed' ? status : 'queued',
    results,
    pictures,
    error: String(body.error ?? ''),
    credits: Number(body.credits) || 0,
    live: Number(body.live) || 0,
    limit: Number(body.limit) || 0,
  }
}

export async function upscaleStatus(runId: string): Promise<UpscaleRun> {
  const response = await fetch(`${API_BASE}/api/skills/run?id=${encodeURIComponent(runId)}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await reason(response))
  return readRun(await response.json(), runId)
}

/** Between polls. */
const EVERY = 3000
/** Consecutive failed polls before the asking is treated as the problem. */
const MAX_MISSES = 5
/**
 * Give up after this long. Longer than a picture run's twelve minutes: ten
 * pictures may honestly take longer, and the Worker keeps the run alive with
 * a heartbeat while it works (routes/upscale.ts).
 */
const GIVE_UP_AFTER = 20 * 60 * 1000

/**
 * Start to finish, by asking. `onProgress` hears every answer, so a surface
 * can put each picture down as soon as it is made rather than all at the end.
 */
export async function awaitUpscale(
  runId: string,
  onProgress?: (run: UpscaleRun) => void,
  signal?: AbortSignal,
): Promise<UpscaleRun> {
  const startedAt = Date.now()
  let misses = 0
  const dead = (error: string): UpscaleRun => ({
    id: runId,
    status: 'failed',
    results: [],
    pictures: new Map(),
    error,
    credits: 0,
    live: 0,
    limit: 0,
  })
  for (;;) {
    await new Promise((wake) => setTimeout(wake, EVERY))
    if (signal?.aborted) return dead('Stopped watching this run')
    try {
      const run = await upscaleStatus(runId)
      misses = 0
      onProgress?.(run)
      if (run.status === 'succeeded' || run.status === 'failed') return run
    } catch (error) {
      misses += 1
      console.warn('could not read the upscale', error)
      if (misses >= MAX_MISSES) return dead('Lost track of this run — reload to see if it finished')
    }
    if (Date.now() - startedAt > GIVE_UP_AFTER) {
      return dead('This took longer than it should have — reload to see if it finished')
    }
  }
}

/** One finished run, for the page's list. */
export interface UpscaleHistory {
  id: string
  status: UpscaleRun['status']
  error: string
  credits: number
  model: string
  size: string
  face: boolean
  results: UpscaleResult[]
  createdAt: string
  /** What went in and what came out, by name. */
  pictures: Map<string, UpscaledPicture>
}

export async function listUpscales(projectId: string): Promise<UpscaleHistory[]> {
  const response = await fetch(
    `${API_BASE}/api/skills/runs?project=${encodeURIComponent(projectId)}&skill=upscale`,
    { headers: { Accept: 'application/json', ...(await authHeaders()) } },
  )
  if (!response.ok) throw new Error(await reason(response))
  const rows = (await response.json()) as Record<string, unknown>[]
  return rows.map((row) => {
    const run = readRun({ ...row, pictures: [...asList(row.pictures), ...asList(row.references)] }, String(row.id ?? ''))
    const params = (row.params ?? {}) as Record<string, unknown>
    return {
      id: run.id,
      status: run.status,
      error: run.error,
      credits: run.credits,
      model: String(params.model ?? ''),
      size: String(params.size ?? ''),
      face: params.face === true,
      // Runs from before the results were written have only their inputs.
      results:
        run.results.length > 0
          ? run.results
          : (Array.isArray(row.inputs) ? row.inputs : []).map((one) => ({ input: String(one) })),
      createdAt: String(row.createdAt ?? ''),
      pictures: run.pictures,
    }
  })
}

function asList(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}
